import { useQuery } from "@tanstack/react-query";

import { api, bytes, relativeTime, uptime } from "../api";

interface NodeOut {
  id: string;
  name: string;
  status: string;
  cpu_cores: number | null;
  memory_total_bytes: number | null;
  memory_used_bytes: number | null;
  uptime_seconds: number | null;
  last_seen_at: string | null;
}

interface ClusterOut {
  id: string;
  kind: string;
  name: string;
  endpoint: string;
  verify_tls: boolean;
  status: string;
  last_seen_at: string | null;
  last_error: string | null;
  credentials: { purpose: string; username: string }[];
  nodes: NodeOut[];
}

export function AdminClusters() {
  const { data, isPending } = useQuery({
    queryKey: ["clusters"],
    queryFn: () => api.get<ClusterOut[]>("/api/admin/clusters"),
  });

  return (
    <>
      <div className="page-head">
        <div>
          <p className="label">Infrastructure</p>
          <h1>Clusters and nodes</h1>
        </div>
      </div>

      {isPending && <p className="muted">Loading…</p>}

      <div className="stack">
        {data?.map((cluster) => (
          <section className="panel" key={cluster.id}>
            <div className="spread" style={{ marginBottom: "1rem" }}>
              <div>
                <h2 style={{ marginBottom: "0.1rem" }}>{cluster.name}</h2>
                <p className="row__meta" style={{ marginTop: 0 }}>
                  <span>{cluster.kind}</span>
                  <span>{cluster.endpoint}</span>
                  <span>checked {relativeTime(cluster.last_seen_at)}</span>
                </p>
              </div>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                {!cluster.verify_tls && (
                  <span className="state state--suspended" title="Certificate not verified">
                    tls unverified
                  </span>
                )}
                <span
                  className={`state state--${cluster.status === "active" ? "running" : cluster.status === "unreachable" ? "stopped" : "suspended"}`}
                >
                  {cluster.status}
                </span>
              </div>
            </div>

            {cluster.last_error && (
              <p className="notice notice--error" style={{ marginBottom: "1rem" }}>
                {cluster.last_error}
              </p>
            )}

            {/* Every node in the cluster as one mark, tallest bar = most memory
                in use. Shows at a glance where load actually sits, which a
                table of numbers does not. */}
            {cluster.nodes.length > 0 && (
              <>
                <p className="label">{cluster.nodes.length} nodes · memory in use</p>
                <div className="noderail">
                  {cluster.nodes.map((node) => {
                    const ratio =
                      node.memory_total_bytes && node.memory_used_bytes
                        ? node.memory_used_bytes / node.memory_total_bytes
                        : 0;
                    return (
                      <div
                        key={node.id}
                        data-offline={node.status !== "online"}
                        title={`${node.name} — ${Math.round(ratio * 100)}% of ${bytes(node.memory_total_bytes)}, up ${uptime(node.uptime_seconds)}`}
                      >
                        <i style={{ height: `${Math.max(ratio * 100, 3)}%` }} />
                      </div>
                    );
                  })}
                </div>
                <div className="table-wrap" style={{ marginTop: "1rem" }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Node</th>
                        <th>State</th>
                        <th>Cores</th>
                        <th>Memory</th>
                        <th>Uptime</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cluster.nodes.map((node) => (
                        <tr key={node.id}>
                          <td className="id">{node.name}</td>
                          <td>
                            <span
                              className={`state state--${node.status === "online" ? "running" : "stopped"}`}
                            >
                              {node.status}
                            </span>
                          </td>
                          <td className="id">{node.cpu_cores ?? "—"}</td>
                          <td className="id">
                            {bytes(node.memory_used_bytes)} / {bytes(node.memory_total_bytes)}
                          </td>
                          <td className="id">{uptime(node.uptime_seconds)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        ))}
      </div>
    </>
  );
}
